import { useState } from "react";
import {
  FolderMoveIcon,
  Menu,
  MenuIconButton,
  MenuItem,
  MenuList,
  OverflowMenuIcon,
} from "@hightouch/htui";
import type { AmpLibraryFileFragment } from "src/graphql";
import { FileMenuItems } from "src/pages/projects/library/canvas/file-menus";
import { DownloadFileMenuItem } from "src/pages/projects/library/files/download-file-menu-item";
import { getFileType } from "src/pages/projects/library/files/file-type";
import { useFileActions } from "src/pages/projects/library/files/use-file-actions";
import { MoveFilesPopover } from "src/pages/projects/library/move-files-popover";

type Props = {
  file: AmpLibraryFileFragment;
  onOpen: (file: AmpLibraryFileFragment) => void;
  onDownload: (file: AmpLibraryFileFragment) => void;
  onDeleted: (id: string) => void;
  variant?: "secondary";
};

/** A file's own actions. "Move to…" opens the folder picker under this menu's button, and leaves the checked files alone. */
export function FileMenu({
  file,
  onOpen,
  onDownload,
  onDeleted,
  variant,
}: Props) {
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const actions = useFileActions({ file, onDeleted });
  return (
    <>
      <MoveFilesPopover
        files={[file]}
        isOpen={isMoveOpen}
        // The button opens the menu; only its "Move to…" opens the picker, so the trigger can only close it.
        onOpenChange={(isOpen) => {
          if (!isOpen) setIsMoveOpen(false);
        }}
      >
        {/* Fixed, so the closed list of a card scrolled below the library's own scroll area never grows the page's. */}
        <Menu strategy="fixed">
          <MenuIconButton
            icon={OverflowMenuIcon}
            size="sm"
            variant={variant}
            aria-label={`Show actions for ${file.name || "Untitled"}`}
          />
          <MenuList>
            <MenuItem
              icon={getFileType(file).icon}
              onClick={() => onOpen(file)}
            >
              Open
            </MenuItem>
            <DownloadFileMenuItem file={file} onDownload={onDownload} />
            <MenuItem icon={FolderMoveIcon} onClick={() => setIsMoveOpen(true)}>
              Move to…
            </MenuItem>
            {/* Delete under a rule, then the staff-only Show/Hide, as in the canvas's ···. */}
            <FileMenuItems
              items={[
                actions.deleteMenuItem,
                ...(actions.visibilityMenuItem
                  ? [actions.visibilityMenuItem]
                  : []),
              ]}
            />
          </MenuList>
        </Menu>
      </MoveFilesPopover>
      {actions.confirmation}
    </>
  );
}
